import { computed, ref, watch } from 'vue';
import { refDebounced } from '@vueuse/shared';
import { defineStore, storeToRefs } from 'pinia';

import { ReportTypesEnum } from '@/enums/reportTypes';

import { TODAY, parseDate } from '@/helpers/dateParser';

import { GetCompanyWithEmployeeViewing } from '@/services/Companies/GetCompaniesWithEmployeeViewing';
import { GetCompanyDepartments } from '@/services/Companies/GetCompanyDepartments';
import { PostCompanyLocations } from '@/services/Companies/PostCompanyLocations';
import { GetCompanyLocations } from '@/services/Companies/GetCompanyLocations';
import { PostEmployeeList } from '@/services/Employees/PostEmployeeList';
import { GetEmployeeList } from '@/services/Employees/GetEmployeeList';
import { PostAttendanceLogs } from '@/services/AttendanceLogs/PostAttendanceLogs';
import { PostArchivedAttendanceLogs } from '@/services/AttendanceLogs/PostArchivedAttendanceLogs';
import { GetAttendanceLogsSearchCriteria } from '@/services/AttendanceLogs/GetAttendanceLogsSearchCriteria';
import { GetArchivedAttendanceLogsSearchCriteria } from '@/services/AttendanceLogs/GetArchivedAttendanceLogsSearchCriteria';
import { GetReportsList } from '@/services/Reports/GetReportsList';
import { GetProjects } from '@/services/ProjectTagging/GetProjects';
import { PostCreateAttendanceLogs } from '@/services/AttendanceLogs/PostCreateAttendanceLogs';
import { GetRouteAccess } from '@/services/Users/GetRouteAccess';

import { useAttendanceManagementBatchAddStore } from './attendanceManagementBatchAdd';

export const useAttendanceManagementStore = defineStore(
  'attendanceManagement',
  () => {
    const isAttendanceLogsActive = ref(true);
    const isAttendanceManagementAccessLevelOn = ref(true);

    const companyList = ref([]);
    const selectedCompany = ref({ id: 0, name: '' });
    const selectedCompanyId = ref('');
    const selectedCompanyName = ref('');
    const companyListLoader = ref(false);
    const companySearchString = ref('');
    const isCompanyFirstLoadEmpty = ref(false);
    const isCompanyChanged = ref(false);
    const isLocationChanged = ref(false);

    const getCompanyList = async (
      pageNumber,
      searchString,
      defaultSelected,
    ) => {
      let isReset = searchString != companySearchString.value;

      if (isReset) {
        companySearchString.value = searchString;
        companyList.value = [];
        pageNumber = 1;
      }

      companyListLoader.value = true;

      const rowsPerPage = 10;
      const sortColumn = 'CompanyName';
      const sortOrder = 'asc';

      const result = await GetCompanyWithEmployeeViewing(
        companySearchString.value,
        pageNumber,
        rowsPerPage,
        sortColumn,
        sortOrder,
      );

      if (defaultSelected) {
        selectedCompanyId.value = result.defaultCompanyId;
        selectedCompanyName.value = result.defaultCompanyName;
      }

      companyList.value = isReset
        ? result.paginatedList.data
        : companyList.value.concat(result.paginatedList.data);

      const pagination = result.paginatedList.pagination;

      setCompanyPaginations(pagination.currentPage, pagination.totalPages);

      companyListLoader.value = false;
    };

    const companyListTotalPages = ref(0);
    const companyListCurrentPage = ref(1);
    const setCompanyPaginations = (currentPage, totalPages) => {
      companyListCurrentPage.value = currentPage;
      companyListTotalPages.value = totalPages;
    };

    //Department Filter
    const isFirstLoadEmpty = ref(false);

    watch(selectedCompanyId, async (newVal, oldVal) => {
      if (oldVal) isCompanyChanged.value = true;

      if (newVal) {
        const getCompany = companyList.value.find((val) => val.id === newVal);

        if (getCompany) {
          selectedCompanyName.value = getCompany.companyName;
        }
      }

      resetDepartment();

      await getCompanyDepartments();

      if (departmentList.value.length === 0) {
        isFirstLoadEmpty.value = true;
      } else {
        isFirstLoadEmpty.value = false;
      }
    });

    const getSelectedDepartmentIds = computed(() => {
      return selectedDepartments.value.map((val) => val.id);
    });

    const departmentList = ref([]);
    const selectedDepartments = ref([]);

    const selectedDepartmentsText = computed(() => {
      if (departmentListLoader.value) {
        return 'Loading';
      }

      if (isFirstLoadEmpty.value) {
        return 'No Results';
      }

      if (selectedDepartments.value.length === 0) {
        if (isCompanyChanged.value) {
          isCompanyChanged.value = false;

          return 'Select Department';
        }

        return 'All';
      }

      if (selectedDepartments.value.length === 1) {
        return `${selectedDepartments.value[0].departmentName}`;
      }

      return `${selectedDepartments.value.length} department${
        selectedDepartments.value.length > 1 ? 's' : ''
      } selected`;
    });

    const searchDepartment = ref('');
    const debouncedSearchDepartment = refDebounced(searchDepartment, 500);

    watch(debouncedSearchDepartment, async (newVal) => {
      departmentListLoader.value = true;
      departmentList.value = [];

      if (newVal.length != 0) {
        departmentListCurrentPage.value = 1;

        const params = { SearchString: newVal, PageNumber: 1 };

        await getCompanyDepartments(params);
      } else {
        await getCompanyDepartments();
      }
    });

    const departmentListLoader = ref(false);
    const departmentListCurrentPage = ref(1);
    const departmentListTotalPages = ref(0);

    const getCompanyDepartments = async (params) => {
      departmentListLoader.value = true;

      await GetCompanyDepartments({
        CompanyId: selectedCompanyId.value,
        ...(params ? { ...params } : {}),
      }).then((result) => {
        const data = result.data.map((d) => d);
        departmentList.value = departmentList.value.concat(data);
        departmentListTotalPages.value = result.pagination.totalPages;
        departmentListLoader.value = false;
      });
    };

    const resetDepartment = () => {
      searchDepartment.value = '';
      selectedDepartments.value = [];
      departmentList.value = [];
      departmentListCurrentPage.value = 1;
      departmentListTotalPages.value = 0;
    };

    // Location Filter
    const locationList = ref([]);
    const selectedLocations = ref([]);

    const selectedLocationsText = computed(() => {
      if (locationListLoader.value) {
        return 'Loading';
      }

      if (locationList.value.length === 0) {
        return 'No Location';
      }

      if (selectedDepartmentsText.value == 'Select Department') {
        return 'Select Location';
      }

      if (selectedLocations.value.length === 0) {
        return 'All';
      }

      if (selectedLocations.value.length === 1) {
        return `${selectedLocations.value[0].locationName}`;
      }

      return `${selectedLocations.value.length} location${
        selectedLocations.value.length > 1 ? 's' : ''
      } selected`;
    });

    const locationListLoader = ref(false);
    const locationListCurrentPage = ref(1);
    const locationListTotalPages = ref(0);

    const setCompanyLocations = async (params) => {
      locationListLoader.value = true;

      let searchCriteriaId;

      let locationParams = {
        sortColumn: 'LocationName',
        sortOrder: 'asc',
        RowsPerPage: 100,
        ...params,
      };

      await PostCompanyLocations(selectedCompanyId.value, locationParams).then(
        (data) => {
          searchCriteriaId = data.searchCriteriaId;
        },
      );

      await GetCompanyLocations({
        CompanyId: selectedCompanyId.value,
        Id: searchCriteriaId,
      }).then((result) => {
        const data = result.data.map((d) => d);

        locationList.value = locationList.value.concat(data);
        locationListTotalPages.value = result.pagination.totalPages;
        locationListLoader.value = false;
      });
    };

    const searchLocation = ref('');
    const debouncedSearchLocation = refDebounced(searchLocation, 500);

    watch(debouncedSearchLocation, async (newSearchVal) => {
      if (newSearchVal) {
        locationListLoader.value = true;
        locationList.value = [];

        let params = {
          SearchString: newSearchVal,
          DepartmentIds: getSelectedDepartmentIds.value,
          PageNumber: 1,
        };

        await setCompanyLocations(params);

        locationListCurrentPage.value = 1;
      }
    });

    const debouncedDepartmentIds = refDebounced(getSelectedDepartmentIds, 500);
    const isFirstLoadLocationEmpty = ref(false);

    watch(debouncedDepartmentIds, async (newDepartmentId) => {
      resetLocation();

      if (newDepartmentId) {
        let params = {
          SearchString: searchLocation.value,
          DepartmentIds: getSelectedDepartmentIds.value,
          PageNumber: 1,
        };

        await setCompanyLocations(params);

        if (locationList.value.length === 0) {
          isFirstLoadLocationEmpty.value = true;
        } else {
          isFirstLoadLocationEmpty.value = false;
        }
      }
    });

    const resetLocation = () => {
      searchLocation.value = '';
      selectedLocations.value = [];
      locationList.value = [];
      locationListCurrentPage.value = 1;
    };

    const getSelectedLocationIds = computed(() => {
      return selectedLocations.value.map((val) => val.id);
    });

    // Employee List Filter
    const employeeList = ref([]);
    const selectedEmployees = ref([]);

    const selectedEmployeesText = computed(() => {
      if (employeeListLoader.value) {
        return 'Loading';
      }

      if (employeeList.value.length === 0) {
        return 'No Employee';
      }

      if (selectedEmployees.value.length === 0) {
        if (
          selectedDepartmentsText.value == 'Select Department' ||
          selectedLocationsText.value == 'Select Location'
        ) {
          isLocationChanged.value = false;

          return 'Select Employees';
        }

        return 'All';
      }

      if (selectedEmployees.value.length === 1) {
        return `${selectedEmployees.value[0].lastName}, ${selectedEmployees.value[0].firstName}`;
      }

      return `${selectedEmployees.value.length} employee${
        selectedEmployees.value.length > 1 ? 's' : ''
      } selected`;
    });

    const employeeListLoader = ref(false);
    const employeeListCurrentPage = ref(1);
    const employeeListTotalPages = ref(0);

    const getEmployeeList = async (params) => {
      if (isBatchAddActive.value && clickedApplyFilter.value) {
        employeeList.value = [];
      }

      employeeListLoader.value = true;

      modalEmployeeListLoader.value = true;

      const searchCriteriaId = ref();

      let employeeParams = {
        ...params,
        sortColumn: 'LastName',
        sortOrder: 'asc',
        include: ['WorkInformation'],
      };

      await PostEmployeeList(employeeParams).then((data) => {
        searchCriteriaId.value = data.searchCriteriaId;
      });

      await GetEmployeeList({
        Id: searchCriteriaId.value,
      }).then((result) => {
        const data = result.data.map((d) => d);

        employeeList.value = employeeList.value.concat(data);
        employeeListTotalPages.value = result.pagination.totalPages;
        employeeListLoader.value = false;

        modalEmployeeList.value = employeeList.value;
        modalEmployeeListTotalPage.value = result.pagination.totalPages;
        modalEmployeeListLoader.value = false;
      });
    };

    const searchEmployee = ref('');
    const debouncedSearchEmployee = refDebounced(searchEmployee, 500);

    watch(debouncedSearchEmployee, async (newSearchVal) => {
      employeeListLoader.value = true;
      employeeList.value = [];
      modalEmployeeList.value = [];

      let params = {
        SearchString: newSearchVal,
        CompanyId: selectedCompanyId.value,
        LocationIds: getSelectedLocationIds.value,
        DepartmentIds: getSelectedDepartmentIds.value,
        PageNumber: 1,
      };

      employeeListCurrentPage.value = 1;

      await getEmployeeList(params);
    });

    const resetEmployee = () => {
      searchEmployee.value = '';
      selectedEmployees.value = [];
      employeeList.value = [];
      employeeListCurrentPage.value = 1;
      employeeListTotalPages.value = 0;
    };

    const debouncedLocationIds = refDebounced(getSelectedLocationIds, 500);
    const isFirstLoadEmployeesEmpty = ref(false);

    watch(debouncedLocationIds, async (newLocationId) => {
      if (newLocationId) isLocationChanged.value = true;

      resetEmployee();

      const employeeListParams = {
        DepartmentIds: getSelectedDepartmentIds.value,
        LocationIds: newLocationId,
        CompanyId: selectedCompanyId.value,
      };

      await getEmployeeList(employeeListParams);

      if (employeeList.value.length === 0) {
        isFirstLoadEmployeesEmpty.value = true;
      } else {
        isFirstLoadEmployeesEmpty.value = false;
      }
    });

    const getSelectedEmployeeIds = computed(() => {
      return selectedEmployees.value.map((val) => val.systemId);
    });

    // Search Logs
    const getAttendanceLogsLoader = ref(false);
    const dateFrom = ref();
    const dateTo = ref();
    const attendanceLogType = ref();
    const isTableInitialState = ref(true);

    const attendanceLogs = ref([]);
    const attendanceLogsPagination = ref({
      currentPage: 1,
      totalPages: 0,
      totalItems: 0,
      itemsPerPage: 10,
    });
    const attendanceLogsParams = ref({
      sortColumn: 'Logtime',
      sortOrder: 'asc',
    });

    const getAttendanceManagementLogs = async (params) => {
      attendanceLogsParams.value = { ...attendanceLogsParams.value, ...params };

      let postAttendanceLogs = await PostAttendanceLogs(
        attendanceLogsParams.value,
      );

      GetAttendanceLogsSearchCriteria({
        Id: postAttendanceLogs.searchCriteriaId,
      }).then((response) => {
        if (response.data && response.data.length > 0) {
          attendanceLogs.value = response.data;

          attendanceLogsPagination.value = response.pagination;
        } else {
          attendanceLogs.value = [];

          attendanceLogsPagination.value = {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
          };

          attendanceLogsParams.value = {
            sortColumn: 'Logtime',
            sortOrder: 'asc',
          };
        }

        getAttendanceLogsLoader.value = false;
      });
    };

    const archivedLogs = ref([]);
    const archivedLogsPagination = ref({
      currentPage: 1,
      totalPages: 0,
      totalItems: 0,
      itemsPerPage: 10,
    });
    const archivedLogsParams = ref({
      sortColumn: 'Logtime',
      sortOrder: 'asc',
    });

    const getArchivedAttendanceManagementLogs = async (params) => {
      archivedLogsParams.value = { ...archivedLogsParams.value, ...params };

      let postArchivedAttendanceLogs = await PostArchivedAttendanceLogs(
        archivedLogsParams.value,
      );

      GetArchivedAttendanceLogsSearchCriteria({
        Id: postArchivedAttendanceLogs.searchCriteriaId,
      }).then((response) => {
        if (response.data && response.data.length > 0) {
          archivedLogs.value = response.data;
          archivedLogsPagination.value = response.pagination;
        } else {
          archivedLogs.value = [];
          archivedLogsPagination.value = {
            currentPage: 1,
            totalPages: 0,
            totalItems: 0,
            itemsPerPage: 10,
          };
          archivedLogsParams.value = {
            sortColumn: 'Logtime',
            sortOrder: 'asc',
          };
        }

        setTimeout(() => {
          getAttendanceLogsLoader.value = false;
        }, 500);
      });
    };

    // Exported Files
    const exportedLogs = ref([]);
    const exportedLogsPagination = ref({
      currentPage: 1,
      totalPages: 0,
      totalItems: 0,
      itemsPerPage: 10,
    });
    const exportReportLoader = ref(false);
    const isExportedLogsInitialState = ref(true);
    const getExportedAttendanceManagement = async ({
      PageNumber,
      RowsPerPage,
    }) => {
      exportReportLoader.value = true;
      try {
        const response = await GetReportsList({
          PageNumber,
          RowsPerPage,
          ReportType: ReportTypesEnum.attendanceManagement,
        });
        if (response.errors) {
          exportedLogs.value = [];
          return;
        }
        exportedLogs.value = response.data;
        exportedLogsPagination.value = response.pagination;
      } finally {
        isExportedLogsInitialState.value = false;
        exportReportLoader.value = false;
      }
    };
    const exportFileReportLoading = ref(false);

    // Manage Log
    const manageLogModalState = ref(false);
    const confirmLogModalState = ref(false);
    const modalParams = ref({});
    const modalType = ref('add');
    const isUpdateSaveDisabled = ref(true);
    const manageLogTSL = ref(false);
    const modalEmployeeID = ref();
    const modalEmployeeListLoader = ref(false);
    const modalEmployeeListCurrentPage = ref(1);
    const modalEmployeeListTotalPage = ref(0);
    const modalEmployeeListSearch = ref('');
    const debouncedModalSearchEmployee = refDebounced(
      modalEmployeeListSearch,
      500,
    );
    const modalEmployeeList = ref([]);
    const modalSelectedEmployees = ref('');
    const modalSelectedEmployeesText = ref('Select Employee');

    watch(debouncedModalSearchEmployee, async (newSearchVal) => {
      modalEmployeeListLoader.value = true;
      employeeList.value = [];
      modalEmployeeList.value = [];

      let params = {
        SearchString: newSearchVal,
        CompanyId: selectedCompanyId.value,
        LocationIds: getSelectedLocationIds.value,
        DepartmentIds: getSelectedDepartmentIds.value,
        PageNumber: 1,
      };

      modalEmployeeListCurrentPage.value = 1;

      await getEmployeeList(params);
    });

    const modalProjectListLoader = ref(false);
    const modalProjectListCurrentPage = ref(1);
    const modalProjectListTotalPage = ref(0);
    const modalProjectListSearch = ref(null);
    const modalProjectListSearchEmployeeID = ref(null);
    const debouncedModalSearchProject = refDebounced(
      modalProjectListSearch,
      500,
    );
    const modalProjectList = ref([]);
    const modalSelectedProject = ref('');
    const modalSelectedProjectText = ref('Select Project (Optional)');
    const projectParams = { SortColumn: 'Name', SortOrder: 'asc' };
    const getProjectsViaEmpId = async (payload) => {
      let params = { ...projectParams, ...payload };
      const response = await GetProjects(params);

      if (response.errors) {
        modalProjectList.value = [];
        modalProjectListLoader.value = false;
        return;
      }
      modalProjectList.value = response.data;
      modalProjectListTotalPage.value = response.pagination.totalPages;
      modalProjectListCurrentPage.value = response.pagination.currentPage;
    };

    watch(modalSelectedEmployees, async (newVal, oldVal) => {
      if (newVal.systemId != oldVal.systemId) {
        modalSelectedProjectText.value = 'Select Project (Optional)';
        modalSelectedProject.value = '';
        modalEmployeeID.value = newVal.systemId;
      }
    });

    watch(debouncedModalSearchProject, async (newVal) => {
      if (newVal) {
        modalProjectListLoader.value = true;
        modalProjectList.value = [];

        await getProjectsViaEmpId({
          EmployeeId: modalProjectListSearchEmployeeID.value,
          SearchString: newVal,
          FilterBy: ['Name', 'Code'],
        }).then(() => {
          modalProjectListLoader.value = false;
        });
      }
    });

    const postCreateAttendanceLogs = async (params) => {
      await PostCreateAttendanceLogs(params).then(() => {
        successAddLog.value = true;
      });
    };
    const successAddLog = ref(false);
    // Log Modal
    const selectedLog = ref(null);
    const modalDate = ref({
      date: '',
      isError: false,
    });
    const modalTime = ref({
      time: '',
      isError: false,
    });
    const modalLogMode = ref('');
    const successEditLog = ref(false);
    const successEditLogInfo = ref({});
    const routeAccessWithId = ref({
      isAuthenticated: false,
      isAllowedToAccess: false,
      redirectUrl: '',
      accessLevelStatus: '',
    });
    const getClientWithId = (moduleId) => {
      GetRouteAccess(moduleId).then((response) => {
        routeAccessWithId.value = response;
      });
    };

    const initialDateFrom = ref({
      date: parseDate(TODAY),
      isError: false,
    });

    const initialDateTo = ref({
      date: parseDate(TODAY),
      isError: false,
    });

    const searchBtnParams = ref();
    const isTimeSheetLocked = ref();
    const batchDeleteState = ref(false);
    const isSelectedAll = ref(false);
    const batchSelectedDelete = ref([]);
    const successDeleteLog = ref(false);
    const successDeleteLogInfo = ref([]);
    const confirmDeleteLogModal = ref(false);
    const isFeatureFlagOn = ref(false);
    const enableAttendanceManager = ref(false);
    const editAttendanceManager = ref(false);

    // Batch Add Store
    const batchAddStore = useAttendanceManagementBatchAddStore();
    const { isBatchAddActive, clickedApplyFilter } = storeToRefs(batchAddStore);

    return {
      modalProjectListLoader,
      modalProjectListCurrentPage,
      modalProjectListTotalPage,
      modalProjectListSearch,
      modalProjectListSearchEmployeeID,
      modalProjectList,
      modalSelectedProject,
      modalSelectedProjectText,
      selectedCompanyName,
      isAttendanceLogsActive,
      getCompanyList,
      isAttendanceManagementAccessLevelOn,
      companyList,
      selectedCompany,
      selectedCompanyId,
      companyListLoader,
      isCompanyFirstLoadEmpty,
      companyListTotalPages,
      companyListCurrentPage,
      companySearchString,
      getCompanyDepartments,
      selectedDepartments,
      selectedDepartmentsText,
      searchDepartment,
      departmentList,
      departmentListLoader,
      departmentListCurrentPage,
      departmentListTotalPages,
      isFirstLoadEmpty,
      isCompanyChanged,
      locationList,
      selectedLocations,
      selectedLocationsText,
      setCompanyLocations,
      locationListLoader,
      locationListCurrentPage,
      locationListTotalPages,
      searchLocation,
      selectedEmployeesText,
      employeeList,
      selectedEmployees,
      employeeListLoader,
      employeeListCurrentPage,
      employeeListTotalPages,
      getEmployeeList,
      isFirstLoadLocationEmpty,
      isFirstLoadEmployeesEmpty,
      searchEmployee,
      getAttendanceManagementLogs,
      getArchivedAttendanceManagementLogs,
      getSelectedEmployeeIds,
      getSelectedDepartmentIds,
      getSelectedLocationIds,
      dateFrom,
      dateTo,
      getAttendanceLogsLoader,
      attendanceLogs,
      attendanceLogsPagination,
      archivedLogs,
      archivedLogsPagination,
      attendanceLogType,
      isTableInitialState,
      exportReportLoader,
      getExportedAttendanceManagement,
      exportFileReportLoading,
      exportedLogs,
      exportedLogsPagination,
      isExportedLogsInitialState,
      manageLogModalState,
      confirmLogModalState,
      modalParams,
      modalType,
      isUpdateSaveDisabled,
      manageLogTSL,
      modalEmployeeID,
      modalEmployeeListLoader,
      modalEmployeeListCurrentPage,
      modalEmployeeListTotalPage,
      modalEmployeeListSearch,
      modalEmployeeList,
      modalSelectedEmployees,
      modalSelectedEmployeesText,
      getProjectsViaEmpId,
      postCreateAttendanceLogs,
      successAddLog,
      selectedLog,
      modalDate,
      modalTime,
      modalLogMode,
      successEditLog,
      successEditLogInfo,
      getClientWithId,
      routeAccessWithId,
      initialDateFrom,
      initialDateTo,
      searchBtnParams,
      isTimeSheetLocked,
      batchDeleteState,
      isSelectedAll,
      batchSelectedDelete,
      successDeleteLog,
      successDeleteLogInfo,
      confirmDeleteLogModal,
      isFeatureFlagOn,
      enableAttendanceManager,
      editAttendanceManager,
    };
  },
);
